import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageItem } from '../folder-list/hooks/use-folder-list';
import { SVGS } from '@zonofi/zono-money-design-system';
import { toBlob } from 'html-to-image';

interface ActiveDragState {
  tagId: string | null;
  element: HTMLDivElement | null;
  container: HTMLDivElement | null;
  containerRect: DOMRect | null;
  pointerId: number | null;
  raf: number | null;
  latest: { x: number; y: number } | null;
}

type ZoomPanState = {
  scale: number;
  translate: { x: number; y: number };
};

type CanvasTagType = 'name' | 'sku' | 'price';
type CanvasTagShape = 'rect' | 'pill' | 'burst';

interface CanvasTag {
  id: string;
  type: CanvasTagType;
  text: string;
  x: number;
  y: number;
  color: string;
  shape: CanvasTagShape;
  scale: number;
}

interface SharePayload {
  blob: Blob;
  file: File;
  dataUrl: string;
}

interface ActiveResizeState {
  tagId: string | null;
  element: HTMLDivElement | null;
  container: HTMLDivElement | null;
  initialScale: number;
  initialDistance: number | null;
  pointers: Map<number, { x: number; y: number }>;
  raf: number | null;
}

export const FullscreenPreview: React.FC<{
  imgs: ImageItem[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  imageZoomPan: Record<string, ZoomPanState>;
  isPreviewOpen: boolean;
  setIsPreviewOpen: (isPreviewOpen: boolean) => void;
  styles: {
    readonly [key: string]: string;
  };
  selected: string[];
  folderMeta: {
    id: string;
    name: string;
    date: string;
    items: number;
  };
  handleToggleSelect: (src: string) => void;
  updateZoomPanForImage: (
    src: string,
    updater: (current: ZoomPanState) => ZoomPanState
  ) => void;
}> = (props) => {
  const {
    imgs,
    activeIndex,
    setActiveIndex,
    imageZoomPan,
    isPreviewOpen,
    setIsPreviewOpen,
    styles,
    selected,
    folderMeta,
    handleToggleSelect,
    updateZoomPanForImage,
  } = props;

  const [isEditMode, setIsEditMode] = useState(false);

  const [imageTags, setImageTags] = useState<Record<string, CanvasTag[]>>({});
  const [pendingShare, setPendingShare] = useState(false);
  const [isGeneratingSharePayload, setIsGeneratingSharePayload] =
    useState(false);

  const [activeTagId, setActiveTagId] = useState<string | null>(null);
  const [activeControlTab, setActiveControlTab] = useState<'shape' | 'color'>(
    'shape'
  );

  const activeDragRef = useRef<ActiveDragState>({
    tagId: null,
    element: null,
    container: null,
    containerRect: null,
    pointerId: null,
    raf: null,
    latest: null,
  });

  const imageZoomPanRef = useRef<Record<string, ZoomPanState>>({});
  const createDefaultZoomPan = () => ({
    scale: 1,
    translate: { x: 0, y: 0 },
  });

  const getTagsForImage = (src?: string) => {
    if (!src) return [];
    return imageTags[src] ?? [];
  };

  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const suppressCanvasGesturesRef = useRef(false);
  // Maintain refs for all selected image containers

  const getCurrentImageZoomPan = useCallback(() => {
    const activeImage = imgs[activeIndex];
    if (!activeImage) {
      return createDefaultZoomPan();
    }
    return (
      imageZoomPanRef.current[activeImage.src] ||
      imageZoomPan[activeImage.src] ||
      createDefaultZoomPan()
    );
  }, [activeIndex, imgs, imageZoomPan]);

  // Local refs for instant gesture response
  const liveScaleRef = useRef(1);
  const liveTranslateRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [rerender, forceRerender] = useState(0); // Used to update transform per frame

  // Determine what to render (gesture in progress: use refs; idle: use state)
  const [isGesture, setIsGesture] = useState(false);
  // Helper to get current values
  const currentZoomPan = getCurrentImageZoomPan();

  const handleTagSelection = (tagId: string | null) => {
    setActiveTagId(tagId);
    if (tagId) {
      setActiveControlTab('shape');
    }
  };
  // Always update refs from state on image open or activeIndex switch
  useEffect(() => {
    liveScaleRef.current = currentZoomPan.scale;
    liveTranslateRef.current = { ...currentZoomPan.translate };
    forceRerender((v) => v + 1);
    setIsGesture(false);
  }, [activeIndex, isPreviewOpen]);

  // Use the live refs if isGesture, otherwise persisted state
  const imgScale = isGesture ? liveScaleRef.current : currentZoomPan.scale;
  const imgTranslate = isGesture
    ? liveTranslateRef.current
    : currentZoomPan.translate;

  // Pinch state for correct scaling
  const pinchStateRef = useRef<{
    initialDistance: number;
    initialScale: number;
  } | null>(null);

  const lastTouches = useRef<Array<{ x: number; y: number }>>([]);
  const lastPan = useRef({ x: 0, y: 0 });

  const imageCanvasRef = useRef<HTMLDivElement | null>(null);

  const isCanvasInteractionSuppressed = () => suppressCanvasGesturesRef.current;

  const STORY_DURATION = 10000; // 10 seconds
  const [storyProgress, setStoryProgress] = useState(0); // 0..1
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastStartTimeRef = useRef<number | null>(null);

  const activeResizeRef = useRef<ActiveResizeState>({
    tagId: null,
    element: null,
    container: null,
    initialScale: 1,
    initialDistance: null,
    pointers: new Map(),
    raf: null,
  });

  // Auto-advance stories on timer
  useEffect(() => {
    if (!isPreviewOpen || imgs.length === 0 || isEditMode) return;
    setStoryProgress(0);
    lastStartTimeRef.current = Date.now();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const since = Date.now() - (lastStartTimeRef.current ?? 0);
      setStoryProgress(Math.min(since / STORY_DURATION, 1));
    }, 50);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [activeIndex, isPreviewOpen, imgs.length, isEditMode]);

  // Advance story on full
  useEffect(() => {
    if (!isPreviewOpen) return;
    if (storyProgress >= 1) {
      if (activeIndex < imgs.length - 1) {
        setActiveIndexWithRestart(activeIndex + 1); // Use the correct navigation callback
      } else {
        setIsPreviewOpen(false); // Optionally close, or reset to 0
      }
    }
  }, [storyProgress, imgs.length, isPreviewOpen]); // Remove activeIndex from deps

  const closePreview = () => setIsPreviewOpen(false);

  const colorPalette = [
    '#00000099',
    '#000',
    '#32A7E8',
    '#1E88E5',
    '#0067FF',
    '#1DB58F',
    '#FFB800',
    '#A04BFF',
    '#FF6F61',
    '#FFFFFF',
    '#FF3B30',
    '#34C759',
    '#FF9500',
    '#AF52DE',
    '#5AC8FA',
    '#FFD60A',
  ];

  const shapeOptions: { value: CanvasTagShape; label: string }[] = [
    { value: 'rect', label: 'Rectangle' },
    { value: 'pill', label: 'Pill' },
    { value: 'burst', label: 'Burst' },
  ];

  const renderShapePreview = (shape: CanvasTagShape) => {
    if (shape === 'burst') {
      return (
        <span
          className={styles.shapePreviewBurst}
          style={
            {
              WebkitMaskImage: `url(${SVGS.CanvasShapeActive})`,
              maskImage: `url(${SVGS.CanvasShapeActive})`,
            } as React.CSSProperties
          }
        />
      );
    }
    if (shape === 'pill') {
      return <span className={styles.shapePreviewPill} />;
    }
    return <span className={styles.shapePreviewRect} />;
  };

  const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

  const blobToDataUrl = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

  const resetActiveResizeState = () => {
    const resizeState = activeResizeRef.current;
    if (resizeState.raf !== null) {
      cancelAnimationFrame(resizeState.raf);
    }
    resizeState.tagId = null;
    resizeState.element = null;
    resizeState.container = null;
    resizeState.initialScale = 1;
    resizeState.initialDistance = null;
    resizeState.pointers.clear();
    resizeState.raf = null;
  };

  const isLightColor = (hex: string) => {
    const normalized = hex.replace('#', '');
    const bigint = parseInt(normalized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 180;
  };

  const captureCanvasImage = useCallback(
    async (containerRef?: HTMLDivElement | null): Promise<SharePayload> => {
      // Instead of using stale/react state, get the ACTUAL canvas div rendered onscreen
      const liveNode = document.querySelector(
        `.${styles.imageCanvas}`
      ) as HTMLDivElement | null;
      if (!liveNode) throw new Error('Preview canvas not available');

      // Deep clone the DOM subtree
      const clone = liveNode.cloneNode(true) as HTMLDivElement;

      // Helper: Copy ALL computed styles from one element to another
      function copyComputedStyles(source: Element, target: HTMLElement) {
        const style = window.getComputedStyle(source);
        for (let i = 0; i < style.length; ++i) {
          const name = style[i];
          target.style.setProperty(
            name,
            style.getPropertyValue(name),
            style.getPropertyPriority(name)
          );
        }
      }

      // Copy previewImage <img> exact styles (clone has the same className selector)
      const liveImg = liveNode.querySelector(
        '[data-preview-image="true"]'
      ) as HTMLImageElement | null;
      const cloneImg = clone.querySelector(
        '[data-preview-image="true"]'
      ) as HTMLImageElement | null;
      if (liveImg && cloneImg) {
        copyComputedStyles(liveImg, cloneImg);
        // Explicitly copy 'transform' and critical attributes to clone (zoom/pan)
        cloneImg.style.transform = liveImg.style.transform;
        cloneImg.style.objectFit = liveImg.style.objectFit;
        // Copy width and height in pixels for better fidelity
        if (liveImg.width && liveImg.height) {
          cloneImg.width = liveImg.width;
          cloneImg.height = liveImg.height;
        }
      }

      // FORCE: Solid background and visible styling for html-to-image snapshot
      clone.style.background = '#fff'; // fallback white
      clone.style.borderRadius = '16px';
      clone.style.overflow = 'hidden';
      clone.style.display = 'block';
      clone.style.opacity = '1';
      // Remove mask-image/WebkitMaskImage for html-to-image (as fallback, optional)
      clone.style.webkitMaskImage = 'none';
      clone.style.maskImage = 'none';

      // Copy wrapper (overflow etc) to clone
      const liveWrapper = liveImg?.closest(
        '[class*="imageWrapper"]'
      ) as HTMLElement | null;
      const cloneWrapper = cloneImg?.closest(
        '[class*="imageWrapper"]'
      ) as HTMLElement | null;
      if (liveWrapper && cloneWrapper)
        copyComputedStyles(liveWrapper, cloneWrapper);

      // Tag overlays: copy style for exact match
      const liveTags = liveNode.querySelectorAll('[class*="canvasTag"]');
      const cloneTags = clone.querySelectorAll('[class*="canvasTag"]');
      liveTags.forEach((liveTag, idx) => {
        if (cloneTags[idx])
          copyComputedStyles(
            liveTag as HTMLElement,
            cloneTags[idx] as HTMLElement
          );
      });

      // Use the onscreen width (client rendered size always matches!)
      const rect = liveNode.getBoundingClientRect();
      const targetWidth = Math.round(rect.width);
      const targetHeight = Math.round(rect.height);
      const pixelRatio = Math.min(Math.max(window.devicePixelRatio || 1, 2), 3);
      // handle background color
      const computedStyles = window.getComputedStyle(liveNode);

      const bgRect = document.createElement('div');
      bgRect.style.position = 'absolute';
      bgRect.style.inset = '0';
      bgRect.style.zIndex = '99999999';
      bgRect.style.pointerEvents = 'none';
      bgRect.style.borderRadius = '16px';
      clone.insertBefore(bgRect, clone.firstChild);

      // Build offscreen wrapper targeting same width/height as live preview
      const offscreenWrapper = document.createElement('div');
      offscreenWrapper.style.width = `${targetWidth}px`;
      offscreenWrapper.style.height = `${targetHeight}px`;
      // Add forced solid background and visible wrapper styling _here_
      offscreenWrapper.style.background = '#fff';
      offscreenWrapper.style.borderRadius = '16px';
      offscreenWrapper.style.overflow = 'hidden';
      offscreenWrapper.style.display = 'block';
      offscreenWrapper.style.opacity = '1';
      offscreenWrapper.appendChild(clone);
      document.body.appendChild(offscreenWrapper);

      // Wait for DOM to paint, ensure all images loaded
      await new Promise((resolve) => setTimeout(resolve, 30));
      const imgsInClone = Array.from(clone.querySelectorAll('img'));
      await Promise.all(
        imgsInClone
          .filter((img) => img && !img.complete)
          .map(
            (img) =>
              new Promise((resolve, reject) => {
                img.onload = () => resolve(null);
                img.onerror = reject;
              })
          )
      );

      try {
        const blob = await toBlob(offscreenWrapper, {
          cacheBust: true,
          pixelRatio,
          width: targetWidth,
          height: targetHeight,
          style: {
            width: `${targetWidth}px`,
            height: `${targetHeight}px`,
            transform: 'none',
            transformOrigin: 'top left',
          },
        });
        if (!blob) throw new Error('Failed to generate edited image');
        const dataUrl = await blobToDataUrl(blob);
        const fileName = `zotok-canvas-${Date.now()}.png`;
        const file = new File([blob], fileName, { type: 'image/png' });
        return { blob, file, dataUrl };
      } finally {
        offscreenWrapper.remove();
      }
    },
    [blobToDataUrl]
  );

  const updateTagForImage = useCallback(
    (src: string, tagId: string, updater: (tag: CanvasTag) => CanvasTag) => {
      setImageTags((prev) => {
        const currentTags = prev[src] ?? [];
        const updatedTags = currentTags.map((tag) =>
          tag.id === tagId ? updater(tag) : tag
        );
        return {
          ...prev,
          [src]: updatedTags,
        };
      });
    },
    []
  );

  const captureImageBySrc = useCallback(
    async (src: string, targetIndex: number): Promise<File | null> => {
      // Only change activeIndex if it's different to avoid unnecessary re-renders
      // that might reset zoom state
      if (activeIndex !== targetIndex) {
        setActiveIndex(targetIndex);
        // Wait for image switch to complete
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(resolve, 150); // Give time for image switch
            });
          });
        });
      }

      // Only clear tag selection if needed, don't force re-render
      if (activeTagId !== null) {
        setActiveTagId(null);
        setActiveControlTab('shape');
        // Small delay to let state update
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }

      // Wait for the preview to render
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 100); // Give time for DOM update and image load
          });
        });
      });

      try {
        const payload = await captureCanvasImage();
        if (payload?.file) {
          return payload.file;
        }
        // if (payload?.dataUrl) {
        //   return base64ToFile(
        //     payload.dataUrl,
        //     `zotok-canvas-${targetIndex}.png`
        //   );
        // }
        return null;
      } catch (error) {
        console.error(`Failed to capture image ${src}:`, error);
        return null;
      }
    },
    [activeIndex, activeTagId, captureCanvasImage]
  );

  const doShare = useCallback(async () => {
    if (
      isGeneratingSharePayload ||
      imgs.length === 0 ||
      selected.length === 0
    ) {
      return;
    }
    setIsGeneratingSharePayload(true);

    try {
      if (activeTagId) {
        setActiveTagId(null);
        setActiveControlTab('shape');
        await new Promise((resolve) =>
          requestAnimationFrame(() => resolve(null))
        );
      }

      // Save original state
      const originalActiveIndex = activeIndex;
      const originalActiveTagId = activeTagId;
      const originalControlTab = activeControlTab;
      const wasPreviewOpen = isPreviewOpen;

      // Ensure preview is open to capture images
      if (!wasPreviewOpen) {
        setIsPreviewOpen(true);
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 100); // Wait for preview to open
          });
        });
      }

      // Capture all selected images sequentially to avoid conflicts
      // Store as pairs to maintain file-image relationship
      const filesWithImages: {
        file: File;
        image: (typeof imgs)[0];
        src: string;
      }[] = [];
      try {
        for (const src of selected) {
          const imageIndex = imgs.findIndex((img) => img.src === src);
          if (imageIndex < 0) continue;
          const image = imgs[imageIndex];
          const file = await captureImageBySrc(src, imageIndex);
          if (file && image) {
            filesWithImages.push({ file, image, src });
          }
        }
      } finally {
        // Restore original state
        setActiveIndex(originalActiveIndex);
        setActiveTagId(originalActiveTagId);
        setActiveControlTab(originalControlTab);
        if (!wasPreviewOpen) {
          setIsPreviewOpen(false);
        }
      }

      if (filesWithImages.length === 0) {
        console.error('No images were captured');
        setIsGeneratingSharePayload(false);
        return;
      }

      // Prepare all files and create text with individual linkUrls for each file
      const allFiles = filesWithImages.map(({ file }) => file);

      const textLines = filesWithImages.map(({ image }, index) => {
        if (image.linkUrl) {
          return `${image.name || 'Product'}: ${image.linkUrl}`;
        }
        return `${image.name || 'Product'}`;
      });

      let shareText = '';

      const image = filesWithImages[0].image;
      if (filesWithImages.length === 1) {
        shareText = `${image.name}: ${image.linkUrl}`;
      } else {
        shareText = `${textLines.join('\n')}`;
      }

      const shareTitle =
        filesWithImages.length > 1
          ? `${filesWithImages.length} Products • ZoTok Catalogue`
          : 'ZoTok Catalogue';

      if (allFiles.length !== selected.length) {
        alert(
          `Sharing mismatch: Will share ${allFiles.length}, Selected ${selected.length}. May indicate capture failures. Check browser/mobile permissions for canvas/image capture. Try again if issue persists.`
        );
      }
      allFiles.forEach((file, idx) => {
        if (
          !(file instanceof File) ||
          !file.name ||
          !file.type.startsWith('image/')
        ) {
          alert(
            `File at index ${idx} is not a proper image file: ${JSON.stringify(file)}. This may break share. Try reloading or updating your browser/app.`
          );
        }
        console.log(
          'File info:',
          idx,
          file && file.name,
          file && file.type,
          file && file.size
        );
      });

      try {
        await navigator.share({
          files: allFiles,
          text: `Check out these products:\n${shareText}`,
          title: `Check out these products:\n${shareText}`,
        });
        setIsGeneratingSharePayload(false);
        return;
      } catch (error) {
        const err = error as DOMException;
        if (err?.name === 'AbortError') {
          setIsGeneratingSharePayload(false);
          return;
        }
        console.warn(
          'Native share failed, falling back to manual share',
          error
        );
      }
    } catch (error) {
      console.error('Failed to prepare share payload', error);
    } finally {
      setIsGeneratingSharePayload(false);
    }
  }, [
    activeTagId,
    activeIndex,
    activeControlTab,
    captureImageBySrc,
    imgs,
    isGeneratingSharePayload,
    isPreviewOpen,
    selected,
  ]);

  const handleShareClick = useCallback(() => {
    if (
      isGeneratingSharePayload ||
      pendingShare ||
      imgs.length === 0 ||
      selected.length === 0
    ) {
      return;
    }
    if (!isPreviewOpen) {
      setPendingShare(true);
      setIsPreviewOpen(true);
      return;
    }
    void doShare();
  }, [
    doShare,
    imgs.length,
    isGeneratingSharePayload,
    isPreviewOpen,
    pendingShare,
    selected.length,
  ]);

  useEffect(() => {
    if (isPreviewOpen && pendingShare && !isGeneratingSharePayload) {
      setPendingShare(false);
      void doShare();
    }
  }, [doShare, isGeneratingSharePayload, isPreviewOpen, pendingShare]);

  useEffect(
    () => () => {
      const dragState = activeDragRef.current;
      if (dragState.raf !== null) {
        cancelAnimationFrame(dragState.raf);
        dragState.raf = null;
      }
      const resizeState = activeResizeRef.current;
      if (resizeState.raf !== null) {
        cancelAnimationFrame(resizeState.raf);
        resizeState.raf = null;
      }
      resetActiveResizeState();
    },
    []
  );

  const handleAddTag = (type: CanvasTagType) => {
    const activeImage = imgs[activeIndex];
    if (!activeImage) return;
    const { src, name, sku, price, linkUrl } = activeImage;

    let tagText = '';
    switch (type) {
      case 'name':
        tagText = name;
        break;
      case 'sku':
        tagText = sku;
        break;
      case 'price':
        tagText = `₹${price}`;
        break;
      default:
        break;
    }

    let nextTagId: string | null = null;
    setImageTags((prev) => {
      const currentTags = prev[src] ?? [];
      const existing = currentTags.find((tag) => tag.type === type);
      if (existing) {
        nextTagId = existing.id;
        return prev;
      }
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${type}-${Date.now()}`;
      nextTagId = id;
      const newTag: CanvasTag = {
        id,
        type,
        text: tagText,
        x: 50,
        y: 75,
        color: '#00000099',
        shape: 'rect',
        scale: 1,
      };
      return {
        ...prev,
        [src]: [...currentTags, newTag],
      };
    });
    setActiveTagId(nextTagId);
    setActiveControlTab('shape');
  };

  const handleTagRemoval = (tagId: string) => {
    const activeImage = imgs[activeIndex];
    if (!activeImage) return;
    const { src } = activeImage;
    setImageTags((prev) => {
      const currentTags = prev[src] ?? [];
      const updated = currentTags.filter((tag) => tag.id !== tagId);
      const nextState = { ...prev, [src]: updated };
      if (updated.length === 0) {
        delete nextState[src];
      }
      return nextState;
    });
    if (activeTagId === tagId) {
      setActiveTagId(null);
    }
  };

  const handleTagPositionUpdate = useCallback(
    (tagId: string, xPercent: number, yPercent: number) => {
      const activeImage = imgs[activeIndex];
      if (!activeImage) return;
      updateTagForImage(activeImage.src, tagId, (tag) => ({
        ...tag,
        x: clamp(xPercent, 5, 95),
        y: clamp(yPercent, 5, 95),
      }));
    },
    [activeIndex, imgs, updateTagForImage]
  );

  const handleShapeChange = (shape: CanvasTagShape) => {
    if (!activeTagId) return;
    const activeImage = imgs[activeIndex];
    if (!activeImage) return;
    updateTagForImage(activeImage.src, activeTagId, (tag) => ({
      ...tag,
      shape,
    }));
  };

  const handleColorChange = (color: string) => {
    if (!activeTagId) return;
    const activeImage = imgs[activeIndex];
    if (!activeImage) return;
    updateTagForImage(activeImage.src, activeTagId, (tag) => ({
      ...tag,
      color,
    }));
  };

  const handleScaleChange = useCallback(
    (tagId: string, scale: number) => {
      const activeImage = imgs[activeIndex];
      if (!activeImage) return;
      const clampedScale = clamp(scale, 0.5, 3);
      updateTagForImage(activeImage.src, tagId, (tag) => ({
        ...tag,
        scale: clampedScale,
      }));
    },
    [activeIndex, imgs, updateTagForImage]
  );

  const getDistance = (
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isCanvasInteractionSuppressed()) {
      return;
    }
    if (e.touches.length === 2) {
      setIsGesture(true);
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      pinchStateRef.current = {
        initialDistance: dist,
        initialScale: liveScaleRef.current,
      };
      lastTouches.current = [
        { x: e.touches[0].clientX, y: e.touches[0].clientY },
        { x: e.touches[1].clientX, y: e.touches[1].clientY },
      ];
    }
    if (e.touches.length === 1 && liveScaleRef.current > 1) {
      setIsGesture(true);
      lastTouches.current = [
        { x: e.touches[0].clientX, y: e.touches[0].clientY },
      ];
      lastPan.current = { ...liveTranslateRef.current };
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isCanvasInteractionSuppressed()) {
      return;
    }
    if (e.touches.length === 2 && pinchStateRef.current) {
      setIsGesture(true);
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const { initialDistance, initialScale } = pinchStateRef.current;
      let nextScale = (dist / initialDistance) * initialScale;
      nextScale = Math.min(Math.max(nextScale, 1), 4); // Clamp
      liveScaleRef.current = nextScale;
      forceRerender((v) => v + 1);
      lastTouches.current = [
        { x: e.touches[0].clientX, y: e.touches[0].clientY },
        { x: e.touches[1].clientX, y: e.touches[1].clientY },
      ];
      return;
    }
    if (e.touches.length === 1 && liveScaleRef.current > 1) {
      setIsGesture(true);
      e.preventDefault();
      const { x: sx, y: sy } = lastTouches.current[0];
      const deltaX = e.touches[0].clientX - sx;
      const deltaY = e.touches[0].clientY - sy;
      const container = imageCanvasRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const maxX = (rect.width * liveScaleRef.current) / 2;
        const maxY = (rect.height * liveScaleRef.current) / 2;
        const newX = clamp(lastPan.current.x + deltaX, -maxX, maxX);
        const newY = clamp(lastPan.current.y + deltaY, -maxY, maxY);
        liveTranslateRef.current = { x: newX, y: newY };
      } else {
        liveTranslateRef.current = {
          x: lastPan.current.x + deltaX,
          y: lastPan.current.y + deltaY,
        };
      }
      forceRerender((v) => v + 1);
    }
  };

  const persistLiveZoomPan = () => {
    const activeImg = imgs[activeIndex];
    if (!activeImg) return;
    updateZoomPanForImage(activeImg.src, () => ({
      scale: liveScaleRef.current,
      translate: { ...liveTranslateRef.current },
    }));
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isCanvasInteractionSuppressed()) {
      return;
    }
    if (e.touches.length === 0) {
      // Persist to parent state after gesture ends
      persistLiveZoomPan();
      setIsGesture(false);
    }
  };

  // Mouse events handlers—same idea:
  const isMouseDown = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isCanvasInteractionSuppressed()) {
      return;
    }
    if (liveScaleRef.current > 1) {
      setIsGesture(true);
      isMouseDown.current = true;
      lastMousePos.current = { x: e.clientX, y: e.clientY };
      lastPan.current = { ...liveTranslateRef.current };
    }
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isCanvasInteractionSuppressed()) {
      return;
    }
    if (isMouseDown.current) {
      setIsGesture(true);
      if (liveScaleRef.current > 1) {
        const deltaX = e.clientX - lastMousePos.current.x;
        const deltaY = e.clientY - lastMousePos.current.y;
        const container = imageCanvasRef.current;
        if (container) {
          const rect = container.getBoundingClientRect();
          const maxX = (rect.width * liveScaleRef.current) / 2;
          const maxY = (rect.height * liveScaleRef.current) / 2;
          const newX = clamp(lastPan.current.x + deltaX, -maxX, maxX);
          const newY = clamp(lastPan.current.y + deltaY, -maxY, maxY);
          liveTranslateRef.current = { x: newX, y: newY };
        }
      }
      forceRerender((v) => v + 1);
    }
  };
  const handleMouseUp = () => {
    if (isCanvasInteractionSuppressed()) {
      return;
    }
    if (isGesture) {
      persistLiveZoomPan();
      isMouseDown.current = false;
      setIsGesture(false);
    }
  };
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (isCanvasInteractionSuppressed()) {
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      let nextScale = clamp(liveScaleRef.current + delta, 1, 4);
      liveScaleRef.current = nextScale;
      if (nextScale === 1) {
        liveTranslateRef.current = { x: 0, y: 0 };
      }
      forceRerender((v) => v + 1);
    }
  };

  const applyLatestDragPosition = () => {
    const dragState = activeDragRef.current;
    if (dragState.raf !== null) {
      cancelAnimationFrame(dragState.raf);
      dragState.raf = null;
    }
    if (dragState.element && dragState.latest) {
      dragState.element.style.left = `${dragState.latest.x}%`;
      dragState.element.style.top = `${dragState.latest.y}%`;
    }
  };

  const resetActiveDragState = () => {
    const dragState = activeDragRef.current;
    if (dragState.raf !== null) {
      cancelAnimationFrame(dragState.raf);
    }
    dragState.tagId = null;
    dragState.element = null;
    dragState.container = null;
    dragState.containerRect = null;
    dragState.pointerId = null;
    dragState.raf = null;
    dragState.latest = null;
  };

  const scheduleDragDomUpdate = (xPercent: number, yPercent: number) => {
    const dragState = activeDragRef.current;
    dragState.latest = { x: xPercent, y: yPercent };
    if (dragState.raf !== null) {
      return;
    }
    dragState.raf = requestAnimationFrame(() => {
      dragState.raf = null;
      const currentState = activeDragRef.current;
      if (!currentState.element || !currentState.latest) {
        return;
      }
      currentState.element.style.left = `${currentState.latest.x}%`;
      currentState.element.style.top = `${currentState.latest.y}%`;
    });
  };

  // Manual navigation resets timer
  const setActiveIndexWithRestart = useCallback((idx: number) => {
    setActiveIndex(idx);
    setStoryProgress(0);
    lastStartTimeRef.current = Date.now();
  }, []);

  if (!isPreviewOpen) return null;
  const active = imgs[activeIndex];
  const imageTagsForActive = getTagsForImage(active?.src);

  const onCanvasPointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
    tagId: string
  ) => {
    // Check if this is part of an active pinch gesture (2+ pointers)
    const resizeState = activeResizeRef.current;
    if (resizeState.pointers.size >= 2) {
      // Let resize handler take over - we're in pinch mode
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    handleTagSelection(tagId);
    suppressCanvasGesturesRef.current = true;
    const container = imageContainerRef.current;
    if (!container) return;
    suppressCanvasGesturesRef.current = true;

    const pointerId = event.pointerId;
    const tagElement = event.currentTarget as HTMLDivElement;
    container.setPointerCapture(pointerId);

    const dragState = activeDragRef.current;
    if (dragState.raf !== null) {
      cancelAnimationFrame(dragState.raf);
      dragState.raf = null;
    }
    dragState.tagId = tagId;
    dragState.element = tagElement;
    dragState.container = container;
    dragState.containerRect = container.getBoundingClientRect();
    dragState.pointerId = pointerId;
    dragState.latest = null;

    const moveHandler = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const currentState = activeDragRef.current;
      const currentContainer = currentState.container;
      if (!currentContainer) return;

      if (moveEvent.cancelable) {
        moveEvent.preventDefault();
      }

      const rect = currentContainer.getBoundingClientRect();
      currentState.containerRect = rect;

      if (!rect.width || !rect.height) return;

      const rawX = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      const rawY = ((moveEvent.clientY - rect.top) / rect.height) * 100;
      const clampedX = clamp(rawX, 5, 95);
      const clampedY = clamp(rawY, 5, 95);

      scheduleDragDomUpdate(clampedX, clampedY);
    };

    const handlePointerEnd = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== pointerId) return;

      // Check if we're now in resize mode (2 pointers) - if so, let resize handle it
      const resizeState = activeResizeRef.current;
      if (resizeState.pointers.size >= 2) {
        // We're in pinch mode, let resize handler take over
        return;
      }

      document.removeEventListener('pointermove', moveHandler);
      document.removeEventListener('pointerup', handlePointerEnd);
      document.removeEventListener('pointercancel', handlePointerEnd);

      const currentState = activeDragRef.current;
      const latest = currentState.latest;
      const draggedTagId = currentState.tagId;

      if (
        currentState.container &&
        currentState.pointerId === pointerId &&
        currentState.container.hasPointerCapture(pointerId)
      ) {
        currentState.container.releasePointerCapture(pointerId);
      }

      applyLatestDragPosition();

      if (draggedTagId && latest) {
        handleTagPositionUpdate(draggedTagId, latest.x, latest.y);
      }

      resetActiveDragState();
      suppressCanvasGesturesRef.current = false;

      // Clean up pointer from resize state if it was tracked
      if (resizeState.pointers.has(pointerId)) {
        resizeState.pointers.delete(pointerId);
      }
    };

    document.addEventListener('pointermove', moveHandler, { passive: false });
    document.addEventListener('pointerup', handlePointerEnd);
    document.addEventListener('pointercancel', handlePointerEnd);
  };

  const onCanvasWheel = (
    event: React.WheelEvent<HTMLDivElement>,
    tagId: string
  ) => {
    if (!activeTagId || activeTagId !== tagId) return;
    event.preventDefault();
    event.stopPropagation();

    const activeTag = imageTagsForActive.find((tag) => tag.id === tagId);
    if (!activeTag) return;

    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    const newScale = clamp(activeTag.scale + delta, 0.5, 3);
    handleScaleChange(tagId, newScale);
  };

  const onCanvasPointerDownForResize = (
    event: React.PointerEvent<HTMLDivElement>,
    tagId: string
  ) => {
    // Only handle resize if tag is selected and we have 2 pointers
    if (!activeTagId || activeTagId !== tagId) return;

    const resizeState = activeResizeRef.current;
    if (resizeState.pointers.size !== 2) return;

    const container = imageContainerRef.current;
    if (!container) return;

    const pointerId = event.pointerId;
    const tagElement = event.currentTarget.closest(
      `[data-canvas-tag-id="${tagId}"]`
    ) as HTMLDivElement;
    if (!tagElement) return;

    event.preventDefault();
    event.stopPropagation();

    // Start pinch gesture
    const pointersArray = Array.from(resizeState.pointers.values());
    const distance = getDistance(pointersArray[0], pointersArray[1]);
    const activeTag = imageTagsForActive.find((tag) => tag.id === tagId);
    resizeState.tagId = tagId;
    resizeState.element = tagElement;
    resizeState.container = container;
    resizeState.initialScale = activeTag?.scale || 1;
    resizeState.initialDistance = distance;
    container.setPointerCapture(pointerId);
    suppressCanvasGesturesRef.current = true;

    const moveHandler = (moveEvent: PointerEvent) => {
      if (!resizeState.pointers.has(moveEvent.pointerId)) return;

      if (moveEvent.cancelable) {
        moveEvent.preventDefault();
      }

      resizeState.pointers.set(moveEvent.pointerId, {
        x: moveEvent.clientX,
        y: moveEvent.clientY,
      });

      if (resizeState.pointers.size === 2 && resizeState.initialDistance) {
        const pointersArray = Array.from(resizeState.pointers.values());
        const currentDistance = getDistance(pointersArray[0], pointersArray[1]);
        const scaleRatio = currentDistance / resizeState.initialDistance;
        const newScale = clamp(resizeState.initialScale * scaleRatio, 0.5, 3);

        if (resizeState.raf !== null) {
          cancelAnimationFrame(resizeState.raf);
        }
        resizeState.raf = requestAnimationFrame(() => {
          if (resizeState.tagId) {
            handleScaleChange(resizeState.tagId, newScale);
          }
          resizeState.raf = null;
        });
      }
    };

    const handlePointerEnd = (endEvent: PointerEvent) => {
      if (!resizeState.pointers.has(endEvent.pointerId)) return;

      resizeState.pointers.delete(endEvent.pointerId);

      if (
        resizeState.container &&
        resizeState.container.hasPointerCapture(endEvent.pointerId)
      ) {
        resizeState.container.releasePointerCapture(endEvent.pointerId);
      }

      if (resizeState.pointers.size < 2) {
        document.removeEventListener('pointermove', moveHandler);
        document.removeEventListener('pointerup', handlePointerEnd);
        document.removeEventListener('pointercancel', handlePointerEnd);
        // Reset resize state when we drop below 2 pointers
        resetActiveResizeState();
        suppressCanvasGesturesRef.current = false;
      }
    };

    document.addEventListener('pointermove', moveHandler, { passive: false });
    document.addEventListener('pointerup', handlePointerEnd);
    document.addEventListener('pointercancel', handlePointerEnd);
  };

  return (
    <div className={styles.previewOverlay} role="dialog" aria-modal="true">
      <button
        onClick={closePreview}
        className={styles.closeBtn}
        aria-label="Back"
      >
        <img src={SVGS.CloseIcon} aria-label="Close preview" />
      </button>
      {active &&
        (() => {
          const isSelected = selected.includes(active.src);
          return (
            <button
              type="button"
              aria-pressed={isSelected}
              aria-label={isSelected ? 'Deselect' : 'Select'}
              className={`${styles.previewSelectBtn} ${
                isSelected ? styles.customSelectBtnSelected : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleSelect(active.src);
              }}
            >
              <img
                src={isSelected ? SVGS.CheckBoxTick : SVGS.CheckBoxUnTick}
                alt="check"
                className={styles.customSelectIcon}
              />
            </button>
          );
        })()}

      <div className={styles.headerContent}>
        {selected.length > 0 ? (
          <>
            <div className={styles.headerTitle}>{selected.length} Selected</div>
            <div className={styles.headerSub}>{folderMeta?.name ?? ''}</div>
          </>
        ) : (
          <>
            <div className={styles.headerTitle}>{active?.name}</div>
            <div className={styles.headerSub}>Folder: {folderMeta?.name}</div>
          </>
        )}
      </div>
      {active && (
        <div
          className={`${styles.imageCanvas} ${imgScale > 1 ? styles.imageCanvasZoomed : ''}`}
          ref={(node) => {
            imageContainerRef.current = node;
            imageCanvasRef.current = node; // for zoom/pan
          }}
          onClick={() => handleTagSelection(null)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <div
            className={`${styles.imageWrapper} ${imgScale > 1 ? styles.imageWrapperZoomed : ''}`}
          >
            <img
              data-preview-image="true"
              className={styles.previewImage}
              src={active.src}
              alt="preview"
              style={{
                touchAction: 'none',
                transform: `scale(${imgScale}) translate(${imgTranslate.x / imgScale}px, ${imgTranslate.y / imgScale}px)`,
                transformOrigin: 'center center',
                objectFit: imgScale > 1 ? 'contain' : 'cover',
              }}
            />
          </div>
          {imageTagsForActive.map((tag) => {
            const isSelected = activeTagId === tag.id;
            const textColor = isLightColor(tag.color) ? '#111111' : '#ffffff';
            const tagStyle = {
              '--tag-bg-color': tag.color,
              '--tag-text-color': textColor,
            } as React.CSSProperties;

            if (tag.shape === 'burst') {
              tagStyle['--canvas-shape-mask'] =
                `url(${SVGS.CanvasShapeActive})`;
            }
            const tagScale = tag.scale ?? 1;
            return (
              <div
                key={tag.id}
                className={`${styles.canvasTag} ${
                  isSelected ? styles.canvasTagSelected : ''
                }`}
                data-canvas-tag-id={tag.id}
                style={{
                  left: `${tag.x}%`,
                  top: `${tag.y}%`,
                  transform: `translate(-50%, -50%) scale(${tagScale})`,
                  transformOrigin: 'center center',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleTagSelection(tag.id);
                }}
                onPointerDown={(e) => {
                  // For touch devices, track pointers for potential pinch gesture
                  // but allow drag to work normally for single touch
                  if (e.pointerType === 'touch') {
                    const resizeState = activeResizeRef.current;
                    // Track this pointer for potential pinch
                    resizeState.pointers.set(e.pointerId, {
                      x: e.clientX,
                      y: e.clientY,
                    });
                    // If we now have 2 pointers, start resize and cancel drag
                    if (resizeState.pointers.size === 2) {
                      // Cancel any active drag for this tag
                      const dragState = activeDragRef.current;
                      if (
                        dragState.tagId === tag.id &&
                        dragState.pointerId !== null
                      ) {
                        // Release pointer capture and reset drag state
                        if (
                          dragState.container &&
                          dragState.container.hasPointerCapture(
                            dragState.pointerId
                          )
                        ) {
                          dragState.container.releasePointerCapture(
                            dragState.pointerId
                          );
                        }
                        resetActiveDragState();
                      }
                      // Start resize
                      onCanvasPointerDownForResize(e, tag.id);
                    } else {
                      // Single touch - allow drag
                      onCanvasPointerDown(e, tag.id);
                    }
                  } else {
                    // For mouse, just handle drag
                    onCanvasPointerDown(e, tag.id);
                  }
                }}
                onWheel={(e) => {
                  if (isSelected) {
                    onCanvasWheel(e, tag.id);
                  }
                }}
                role="button"
                aria-label={`${tag.type} tag`}
              >
                <div
                  className={`${styles.canvasTagContent} ${
                    tag.shape === 'pill'
                      ? styles.canvasTagPill
                      : tag.shape === 'burst'
                        ? styles.canvasTagBurst
                        : styles.canvasTagRect
                  }`}
                  // Inline font and colors (override CSS vars)
                  style={{
                    ...tagStyle,
                    backgroundColor:
                      tag.shape === 'burst' ? 'transparent' : tag.color,
                    color: textColor,
                  }}
                  aria-pressed={isSelected}
                >
                  <span>{tag.text}</span>
                  {isSelected && (
                    <button
                      type="button"
                      className={styles.canvasTagRemove}
                      aria-label="Remove tag"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTagRemoval(tag.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Right-side Floating Action Buttons */}
      <div className={styles.actionBarRight}>
        <button
          className={styles.actionBtn}
          onClick={() => handleAddTag('name')}
          aria-label="Show product name"
        >
          <img src={SVGS.ProductIcon} alt="Product" />
        </button>
        <button
          className={styles.actionBtn}
          onClick={() => handleAddTag('sku')}
          aria-label="Show SKU"
        >
          <img src={SVGS.SkuTag} alt="Sku" />
        </button>
        <button
          className={styles.actionBtn}
          onClick={() => handleAddTag('price')}
          aria-label="Show Price"
        >
          <img src={SVGS.PriceTag} alt="Price" />
        </button>
      </div>

      {!activeTagId && (
        <div className={styles.fabStack}>
          <button
            className={styles.fab}
            onClick={closePreview}
            aria-label="Back to grid"
          >
            <img src={SVGS.MasonryGridIcon} alt="grid" />
          </button>
          <button
            className={styles.fab}
            onClick={handleShareClick}
            aria-label="Share"
            disabled={isGeneratingSharePayload || pendingShare}
            aria-busy={isGeneratingSharePayload || pendingShare}
          >
            {isGeneratingSharePayload || pendingShare ? (
              <span className={styles.loadingSpinner} aria-hidden="true" />
            ) : (
              <img src={SVGS.ForwardIcon} alt="forward" />
            )}
          </button>
        </div>
      )}

      {activeTagId && (
        <div
          className={styles.tagControls}
          role="region"
          aria-label="Canvas tag controls"
        >
          <div className={styles.tagControlsTabs}>
            <button
              type="button"
              className={`${styles.tagControlTab} ${
                activeControlTab === 'shape' ? styles.tagControlTabActive : ''
              }`}
              onClick={() => setActiveControlTab('shape')}
            >
              Shape
            </button>
            <button
              type="button"
              className={`${styles.tagControlTab} ${
                activeControlTab === 'color' ? styles.tagControlTabActive : ''
              }`}
              onClick={() => setActiveControlTab('color')}
            >
              Color
            </button>
          </div>
          <div className={styles.hrLine} />
          <div className={styles.tagControlsContent}>
            {activeControlTab === 'shape' && (
              <div className={styles.shapeOptions}>
                {shapeOptions.map((shape) => (
                  <button
                    type="button"
                    key={shape.value}
                    className={`${styles.shapeOption} ${
                      imageTagsForActive.find((tag) => tag.id === activeTagId)
                        ?.shape === shape.value
                        ? styles.shapeOptionActive
                        : ''
                    }`}
                    onClick={() => handleShapeChange(shape.value)}
                  >
                    {renderShapePreview(shape.value)}
                  </button>
                ))}
              </div>
            )}
            {activeControlTab === 'color' && (
              <div className={styles.colorPalette}>
                {colorPalette.map((color) => {
                  const activeTag = imageTagsForActive.find(
                    (tag) => tag.id === activeTagId
                  );
                  const isActive = activeTag?.color === color;
                  return (
                    <div
                      className={`${
                        isActive
                          ? styles.colorSwatchActive
                          : styles.colorSwatchInActive
                      }`}
                    >
                      <button
                        type="button"
                        key={color}
                        className={`${styles.colorSwatch}`}
                        style={{ backgroundColor: color }}
                        onClick={() => handleColorChange(color)}
                        aria-label={`Set color ${color}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {!activeTagId && (
        <div className={styles.thumbStrip} role="list">
          {imgs.map((im, idx) => {
            const isSelected = selected.includes(im.src);
            const isActive = idx === activeIndex;
            return (
              <div
                key={im.src}
                role="listitem"
                className={`${styles.thumbItem} ${isActive ? styles.thumbActive : ''}`}
                onClick={() => setActiveIndexWithRestart(idx)}
                aria-label={`Preview ${idx + 1}`}
              >
                <img src={im.src} alt={`thumb ${idx + 1}`} />
                <button
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={isSelected ? 'Deselect' : 'Select'}
                  className={`${styles.customSelectBtn} ${isSelected ? styles.customSelectBtnSelected : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleSelect(im.src);
                  }}
                >
                  <img
                    src={isSelected ? SVGS.CheckBoxTick : SVGS.CheckBoxUnTick}
                    alt="check"
                    className={styles.customSelectIcon}
                  />
                </button>
                {isActive && (
                  <svg
                    className={styles.thumbProgressRing}
                    width="70"
                    height="70"
                    style={{
                      position: 'absolute',
                      left: -3,
                      top: -3,
                      pointerEvents: 'none',
                      zIndex: 11,
                    }}
                  >
                    <circle
                      cx="35"
                      cy="35"
                      r="33"
                      stroke={isEditMode ? '#bbb' : '#32a7e8'}
                      strokeWidth="5"
                      fill="none"
                      style={{ opacity: 0.21 }}
                    />
                    <circle
                      cx="35"
                      cy="35"
                      r="33"
                      stroke={isEditMode ? '#bbb' : '#32a7e8'}
                      strokeWidth="5"
                      fill="none"
                      strokeDasharray={2 * Math.PI * 33}
                      strokeDashoffset={2 * Math.PI * 33 * (1 - storyProgress)}
                      style={{
                        transition: isEditMode
                          ? ''
                          : 'stroke-dashoffset 0.2s linear',
                        opacity: 1,
                      }}
                    />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
